import Axios, { type AxiosInstance, type AxiosRequestConfig, type CustomParamsSerializer } from "axios"
import type { PureHttpError, RequestMethods, PureHttpResponse, PureHttpRequestConfig } from "./types.d"
import { stringify } from "qs"
import NProgress from "../progress"
import { getToken, formatToken } from "@/utils/auth"
import { useUserStoreHook } from "@/store/modules/user"

// 相关配置请参考：www.axios-js.com/zh-cn/docs/#axios-request-config-1
const defaultConfig: AxiosRequestConfig = {
  // 请求超时时间
  timeout: 300000,
  headers: {
    Accept: "application/json, text/plain, */*",
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest"
  },
  // 数组格式参数序列化（https://github.com/axios/axios/issues/5142）
  paramsSerializer: {
    serialize: stringify as unknown as CustomParamsSerializer
  }
}

class PureHttp {
  constructor() {
    // 尝试在首次构造时打补丁，统一为所有 ElMessage.error 追加最近一次后端错误信息
    this.patchElMessageErrorOnce()
    this.httpInterceptorsRequest()
    this.httpInterceptorsResponse()
  }

  /** `token`过期后，暂存待执行的请求 */
  private static requests = []

  /** 防止重复刷新`token` */
  private static isRefreshing = false

  /** 防止重复调用`logOut()` */
  private static isLoggingOut = false

  /** 初始化配置对象 */
  private static initConfig: PureHttpRequestConfig = {}

  /** 保存当前`Axios`实例对象 */
  private static axiosInstance: AxiosInstance = Axios.create(defaultConfig)

  /** 仅执行一次：为 ElMessage.error 追加最近一次错误消息 */
  private patchElMessageErrorOnce() {
    const g: any = (globalThis || window) as any
    if (g.__el_message_error_patched__) return
    g.__el_message_error_patched__ = true

    // 动态导入以避免循环依赖
    import("element-plus").then((mod: any) => {
      const ElMessage = mod.ElMessage
      if (!ElMessage || !ElMessage.error) return

      const rawError = ElMessage.error.bind(ElMessage)
      ElMessage.error = (msgOrOpts: any) => {
        try {
          const last = (globalThis as any).__lastApiErrorMsg
          if (typeof msgOrOpts === "string" && last && typeof last === "string") {
            if (!msgOrOpts.includes(last)) {
              return rawError(`${msgOrOpts}: ${last}`)
            }
          }
        } catch (_) {}
        return rawError(msgOrOpts)
      }
    }).catch(() => {})
  }

  /** 重连原始请求 */
  private static retryOriginalRequest(config: PureHttpRequestConfig) {
    return new Promise((resolve) => {
      PureHttp.requests.push((token: string) => {
        config.headers["Authorization"] = formatToken(token)
        resolve(config)
      })
    })
  }

  /** 请求拦截 */
  private httpInterceptorsRequest(): void {
    PureHttp.axiosInstance.interceptors.request.use(
      async (config: PureHttpRequestConfig): Promise<any> => {
        // 开启进度条动画
        NProgress.start()
        // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
        if (typeof config.beforeRequestCallback === "function") {
          config.beforeRequestCallback(config)
          return config
        }
        if (PureHttp.initConfig.beforeRequestCallback) {
          PureHttp.initConfig.beforeRequestCallback(config)
          return config
        }
        /** 请求白名单，放置一些不需要`token`的接口（通过设置请求白名单，防止`token`过期后再请求造成的死循环问题） */
        const whiteList = ["/refresh-token", "/login"]
        return whiteList.some((url) => config.url.endsWith(url))
          ? config
          : new Promise((resolve) => {
              const data = getToken()
              if (data) {
                const now = new Date().getTime()
                const expired = parseInt(data.expires) - now <= 0
                if (expired) {
                  if (!PureHttp.isRefreshing) {
                    PureHttp.isRefreshing = true
                    // token过期刷新
                    useUserStoreHook()
                      .handRefreshToken({ refreshToken: data.refreshToken })
                      .then((res) => {
                        const token = res.data.accessToken
                        config.headers["Authorization"] = formatToken(token)
                        PureHttp.requests.forEach((cb) => cb(token))
                        PureHttp.requests = []
                      })
                      .finally(() => {
                        PureHttp.isRefreshing = false
                      })
                  }
                  resolve(PureHttp.retryOriginalRequest(config))
                } else {
                  config.headers["Authorization"] = formatToken(data.accessToken)
                  resolve(config)
                }
              } else {
                resolve(config)
              }
            })
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  /** 响应拦截 */
  private httpInterceptorsResponse(): void {
    const instance = PureHttp.axiosInstance
    instance.interceptors.response.use(
      (response: PureHttpResponse) => {
        const $config = response.config
        // 关闭进度条动画
        NProgress.done()

        // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
        if (typeof $config.beforeResponseCallback === "function") {
          $config.beforeResponseCallback(response)
          return response.data
        }
        if (PureHttp.initConfig.beforeResponseCallback) {
          PureHttp.initConfig.beforeResponseCallback(response)
          return response.data
        }

        // 处理后端标准响应格式 {code, time, msg, data}
        const responseData = response.data

        // 如果响应数据包含code字段，说明是标准格式
        if (responseData && typeof responseData.code !== "undefined") {
          if (responseData.code === 0) {
            // 成功：返回data字段的内容，如果data不存在则返回null（避免undefined导致的问题）
            ;(globalThis as any).__lastApiErrorMsg = ""
            return responseData.data !== undefined ? responseData.data : null
          } else {
            // 失败：抛出错误，包含错误信息
            const backendMsg = responseData.msg || (typeof responseData.data === "string" ? responseData.data : "")
            const finalMsg = backendMsg || "请求失败"
            console.error("API调用失败:", finalMsg, "错误代码:", responseData.code)
            const error: any = new Error(finalMsg)
            error.code = responseData.code
            error.response = response
            ;(globalThis as any).__lastApiErrorMsg = finalMsg
            // error.code = responseData.code
            // error.response = response
            throw error
          }
        }

        // 如果不是标准格式，直接返回原数据（兼容其他格式）
        return responseData
      },
      async (error: PureHttpError) => {
        const $error = error
        $error.isCancelRequest = Axios.isCancel($error)
        // 关闭进度条动画
        NProgress.done()

        // 处理HTTP状态码错误
        if (error?.response?.status) {
          const status = error.response.status

          if (status === 401) {
            // 401: 清空缓存跳转到登录页面
            const requestUrl = error?.config?.url || ""
            const isLogoutRequest = requestUrl.includes("/logout") || requestUrl.includes("/auth/logout")
            
            // 如果是 logout 接口返回 401，不再次调用 logOut()，避免死循环
            if (isLogoutRequest) {
              // 如果已经在登出流程中，直接返回，避免重复处理
              if (PureHttp.isLoggingOut) {
                return Promise.reject($error)
              }
              
              PureHttp.isLoggingOut = true
              console.log("401错误：logout接口返回401，直接跳转登录页面，避免循环调用")
              // 直接清理本地数据并跳转，不调用 logout 接口
              const [{ useUserStoreHook }, { removeToken }, { useMultiTagsStoreHook }, { routerArrays }, { resetRouter, router }] = await Promise.all([
                import("@/store/modules/user"),
                import("@/utils/auth"),
                import("@/store/modules/multiTags"),
                import("@/store/utils"),
                import("@/router")
              ])
              
              const userStore = useUserStoreHook()
              userStore.username = ""
              userStore.roles = []
              removeToken()
              useMultiTagsStoreHook().handleTags("equal", [...routerArrays])
              resetRouter()
              router.push("/login")
              
              // 显示提示信息
              try {
                const { ElMessage } = await import("element-plus")
                ElMessage.error("登录已过期，请重新登录")
              } catch (msgError) {
                console.warn("无法显示错误提示:", msgError)
              }
              
              // 延迟重置标志，确保所有401请求都能被正确处理
              setTimeout(() => {
                PureHttp.isLoggingOut = false
              }, 1000)
              
              return Promise.reject($error)
            }
            
            // 如果已经在登出流程中，直接返回，避免多个401同时触发时重复调用logOut()
            if (PureHttp.isLoggingOut) {
              console.log("401错误：已在登出流程中，跳过重复调用")
              return Promise.reject($error)
            }
            
            PureHttp.isLoggingOut = true
            console.log("401错误：未授权，清空缓存并跳转到登录页面")

            // 动态导入避免循环依赖
            const { useUserStoreHook } = await import("@/store/modules/user")
            useUserStoreHook()
              .logOut()
              .finally(() => {
                // 延迟重置标志，确保所有401请求都能被正确处理
                setTimeout(() => {
                  PureHttp.isLoggingOut = false
                }, 1000)
              })

            // 显示提示信息
            try {
              const { ElMessage } = await import("element-plus")
              ElMessage.error("登录已过期，请重新登录" + (error?.message ? ": " + error.message : ""))
            } catch (msgError) {
              console.warn("无法显示错误提示:", msgError)
            }

            return Promise.reject($error)
          } else if (status === 403) {
            // 403: 显示暂无权限错误
            console.log("403错误：暂无权限")

            // 显示权限错误提示
            try {
              const { ElMessage } = await import("element-plus")
              ElMessage.error("暂无权限" + (error?.message ? ": " + error.message : ""))
            } catch (msgError) {
              console.warn("无法显示错误提示:", msgError)
            }

            return Promise.reject($error)
          }
        }

        // 处理其他HTTP错误
        console.error("HTTP请求错误:", error)

        // 如果是网络错误或超时，添加更友好的错误信息
        const respData: any = error?.response?.data as any
        const backendMsg = respData && (respData.msg || respData.message)
        if (backendMsg && typeof backendMsg === "string") {
          error.message = backendMsg
        } else if (error.code === "NETWORK_ERROR" || error.message?.includes("Network Error")) {
          error.message = "网络连接失败，请检查网络"
        } else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          error.message = "请求超时，请重试"
        } else if (error?.response?.statusText) {
          error.message = error.response.statusText
        }

        ;(globalThis as any).__lastApiErrorMsg = error?.message || ""

        // 所有的响应异常 区分来源为取消请求/非取消请求
        return Promise.reject($error)
      }
    )
  }

  /** 通用请求工具函数 */
  public request<T>(
    method: RequestMethods,
    url: string,
    param?: AxiosRequestConfig,
    axiosConfig?: PureHttpRequestConfig
  ): Promise<T> {
    const config = {
      method,
      url,
      ...param,
      ...axiosConfig
    } as PureHttpRequestConfig

    // 单独处理自定义请求/响应回调
    return new Promise((resolve, reject) => {
      PureHttp.axiosInstance
        .request(config)
        .then((response: undefined) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  /** 单独抽离的`post`工具函数 */
  public post<T, P>(url: string, params?: AxiosRequestConfig<P>, config?: PureHttpRequestConfig): Promise<T> {
    return this.request<T>("post", url, params, config)
  }

  /** 单独抽离的`get`工具函数 */
  public get<T, P>(url: string, params?: AxiosRequestConfig<P>, config?: PureHttpRequestConfig): Promise<T> {
    return this.request<T>("get", url, params, config)
  }
}

export const http = new PureHttp()
